/**
 * DrawingViewer.jsx
 * Full-screen PDF viewer with markup/annotation tools.
 *
 * Input device support:
 *  - Apple Pencil (primary): pressure-sensitive width, palm rejection (never pans)
 *  - Finger single-touch: pans the canvas
 *  - Finger two-touch:    pinch-to-zoom (updates PDF render scale)
 *  - Mouse:               draws with fixed width (desktop fallback)
 *
 * Features:
 *  - PDF.js page rendering (pdfjs-dist 2.16.105, CDN worker)
 *  - Page navigation + page count
 *  - Pinch-to-zoom (two-finger) + on-screen +/- buttons
 *  - Apple Pencil (pressure-sensitive), Highlighter, Text stamp, Eraser
 *  - Colour picker per tool
 *  - Annotations saved per job+page via server API (drawing_annotations table)
 *  - Clear page, Close button
 *
 * S-66 / W-27 (2026-10-06):
 *  - Markup is tied to the drawing revision it was drawn on. Saves and clears send the drawing file on
 *    screen (drawingFile); the server refuses (409) when the job's drawing has changed. Markup drawn on
 *    an earlier or unknown revision is hidden by the server and announced here ("N hidden").
 *  - Markup is kept in PAGE space, not screen space: an offscreen "master" canvas sized to the PDF page's
 *    natural size (scale 1) times REF_SCALE. Strokes are drawn on the screen canvas and, scaled, on the
 *    master; the master is what is saved, and it is drawn scaled onto the screen at any zoom, so markup
 *    saved at one zoom lines up at every other zoom. Saved as a JSON envelope {v:2, space:"pdf-page", ...}.
 *    A plain data URL (saved before W-27, at an unknown zoom) cannot be aligned; it is not drawn and is
 *    announced as legacy markup.
 *  - The markup tools are shown only with edit_job (the server checks the same permission, live).
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import ApiCall from "../../utils/apicall";
import { getAccessToken } from "../../utils/authToken";
import { fetchAuthedMedia } from "../AuthedMedia/AuthedMedia";

// ─── PDF.js lazy load ──────────────────────────────────────────────────────────
let pdfjsLib = null;
async function getPdfjs() {
  if (pdfjsLib) return pdfjsLib;
  pdfjsLib = await import("pdfjs-dist/legacy/build/pdf");
  pdfjsLib.GlobalWorkerOptions.workerSrc =
    "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js";
  return pdfjsLib;
}

// ─── API helpers ───────────────────────────────────────────────────────────────
const API_BASE = process.env.REACT_APP_URL || "";

function authHeaders() {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ─── Constants ─────────────────────────────────────────────────────────────────
const TOOLS = { PENCIL: "pencil", HIGHLIGHT: "highlight", ERASER: "eraser", TEXT: "text" };
const PEN_COLOURS       = ["#E53935", "#1565C0", "#2E7D32", "#000000", "#FF6F00"];
const HIGHLIGHT_COLOURS = ["#FFFF00", "#A5D6A7", "#90CAF9", "#FFCC80"];

// Base widths per tool (multiplied by pressure)
const BASE_WIDTH = { pencil: 2.5, highlight: 22, eraser: 28 };

// W-27: page-space markup. The master canvas is the PDF page at scale 1 times REF_SCALE, capped so it
// stays under mobile Safari's canvas area limit (about 16.7 M pixels).
const REF_SCALE = 2;
const MAX_MASTER_PIXELS = 12000000;
function masterSize(pageWidth, pageHeight) {
  let s = REF_SCALE;
  if (pageWidth * pageHeight * s * s > MAX_MASTER_PIXELS) s = Math.sqrt(MAX_MASTER_PIXELS / (pageWidth * pageHeight));
  return { w: Math.max(1, Math.round(pageWidth * s)), h: Math.max(1, Math.round(pageHeight * s)) };
}
// Saved format: { v: 2, space: "pdf-page", w, h, png }. Anything else is legacy (screen-space, zoom unknown).
function parseMarkup(canvasData) {
  if (typeof canvasData !== "string" || !canvasData) return null;
  if (canvasData.startsWith("{")) {
    try {
      const m = JSON.parse(canvasData);
      if (m && m.v === 2 && m.space === "pdf-page" && typeof m.png === "string") return { kind: "page", png: m.png };
    } catch { /* fall through */ }
  }
  return { kind: "legacy" };
}
function hiddenNotice(hidden) {
  if (!hidden || !hidden.total) return "";
  const parts = [];
  if (hidden.earlierRevision) parts.push(`${hidden.earlierRevision} made on an earlier revision`);
  if (hidden.unknownRevision) parts.push(`${hidden.unknownRevision} made on an earlier or unknown revision`);
  const pages = Array.isArray(hidden.pages) && hidden.pages.length ? ` (page${hidden.pages.length > 1 ? "s" : ""} ${hidden.pages.join(", ")})` : "";
  return `${hidden.total} markup${hidden.total > 1 ? "s" : ""} ${hidden.total > 1 ? "are" : "is"} hidden: ${parts.join(", ")}${pages}. They were drawn on a different drawing and are not shown over this one.`;
}

// Codex #75 r2 P1: markup writes are queued per job at module level, not per viewer, so a viewer that
// is closed with writes pending (a save or a clear) finishes them, in order, before a reopened viewer of
// the same job loads or writes markup.
const writeQueues = new Map(); // jobId -> promise of the last queued write (never rejects)
function enqueueJobWrite(jobId, fn) {
  const key = String(jobId);
  const tail = writeQueues.get(key) || Promise.resolve();
  const run = tail.then(fn, fn);
  const settled = run.catch(() => {});
  writeQueues.set(key, settled);
  settled.then(() => { if (writeQueues.get(key) === settled) writeQueues.delete(key); });
  return run;
}
const jobWritesSettled = (jobId) => writeQueues.get(String(jobId)) || Promise.resolve();

// ─── Styles ────────────────────────────────────────────────────────────────────
const S = {
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    background: "#1a1a1a",
    display: "flex",
    flexDirection: "column",
    userSelect: "none",
    WebkitUserSelect: "none",
    overscrollBehavior: "none",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 12px",
    background: "#222",
    flexWrap: "wrap",
    flexShrink: 0,
    borderBottom: "1px solid #444",
    minHeight: 52,
  },
  btn: (active) => ({
    padding: "6px 12px",
    borderRadius: 6,
    border: "1px solid " + (active ? "#fff" : "#555"),
    background: active ? "#444" : "transparent",
    color: "#fff",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: active ? 600 : 400,
    display: "flex",
    alignItems: "center",
    gap: 4,
    WebkitTapHighlightColor: "transparent",
    touchAction: "manipulation",
  }),
  swatch: (colour, active) => ({
    width: 22,
    height: 22,
    borderRadius: "50%",
    background: colour,
    border: active ? "3px solid #fff" : "2px solid #666",
    cursor: "pointer",
    flexShrink: 0,
    touchAction: "manipulation",
  }),
  // Scroll container — overflow: auto so scrollLeft/scrollTop panning works
  scrollContainer: {
    flex: 1,
    overflow: "auto",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    background: "#1a1a1a",
    // We manage all touch ourselves on the annotation canvas
    touchAction: "none",
  },
  pdfCanvas: {
    display: "block",
    boxShadow: "0 4px 24px rgba(0,0,0,0.6)",
  },
  annotCanvas: {
    position: "absolute",
    top: 0,
    left: 0,
    cursor: "crosshair",
    // Block all default touch — we route finger→pan/pinch, pencil→draw
    touchAction: "none",
  },
  sep:      { width: 1, height: 28, background: "#444", margin: "0 4px" },
  pageInfo: { color: "#bbb", fontSize: 13, margin: "0 8px" },
  statusBar: {
    background: "#111",
    color: "#666",
    fontSize: 12,
    padding: "2px 12px",
    textAlign: "center",
    flexShrink: 0,
  },
  pencilBadge: {
    fontSize: 11,
    color: "#4fc3f7",
    marginLeft: 4,
    padding: "1px 6px",
    borderRadius: 4,
    background: "rgba(79,195,247,0.15)",
    border: "1px solid rgba(79,195,247,0.3)",
  },
};

// ─── Component ─────────────────────────────────────────────────────────────────
// drawingFile: the job's drawing file name (jobs.drawing_pdf) whose PDF is at `url` (S-66).
// canEdit: the user has edit_job; without it the drawing and its markup are view-only.
export default function DrawingViewer({ url, jobId, drawingFile, canEdit = false, onClose }) {
  const pdfRef       = useRef(null);
  const pdfCanvasRef = useRef(null);
  const annotRef     = useRef(null);
  // W-27: page-space markup for the current page: { canvas, page } (see masterSize / parseMarkup).
  const masterRef    = useRef(null);
  // Codex #75 r1 P1: the drawing file the loaded PDF (pdfRef) belongs to. Markup is enabled only when the
  // loaded PDF, the drawingFile prop and the server's current drawing are the same revision.
  const pdfFileRef   = useRef(null);
  // Codex #75 r4 P1: markup is enabled only once the PDF page on screen has finished rendering for the
  // current document, page and zoom (renderedKey === `${docSeq}|${page}|${scale}`).
  const docSeqRef    = useRef(0);
  const renderSeqRef = useRef(0);
  // Codex #75 r5 P1: the PDF.js render task drawing on the canvas now; cancelled and awaited before the
  // next render reuses the canvas (PDF.js refuses two renders on one canvas at once).
  const renderTaskRef = useRef(null);
  const loadSeq      = useRef(0);
  const scrollRef    = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const canvasRef    = useRef(null);

  // Drawing state
  const isDrawing       = useRef(false);
  const lastPoint       = useRef(null);
  const lastPressure    = useRef(0.5);
  const drawPointerId   = useRef(null);   // pointer currently drawing

  // Touch / pan / pinch state
  const touchPointers   = useRef(new Map()); // pointerId → {clientX, clientY}
  const pinchInitDist   = useRef(null);
  const pinchInitScale  = useRef(null);

  const [numPages, setNumPages] = useState(0);
  const [page,     setPage]     = useState(1);
  const [scale,    setScale]    = useState(1.5);
  const [tool,     setTool]     = useState(TOOLS.PENCIL);
  const [penColour,  setPenColour]  = useState(PEN_COLOURS[0]);
  const [hlColour,   setHlColour]   = useState(HIGHLIGHT_COLOURS[0]);
  const [status,   setStatus]   = useState("Loading PDF…");
  const [textInput, setTextInput]   = useState("");
  const [showTextBox, setShowTextBox] = useState(false);
  const [textPos,  setTextPos]  = useState({ x: 0, y: 0 });
  const [pencilDetected, setPencilDetected] = useState(false);
  // Codex WARNING fix: surface annotation save status so users don't lose
  // markup silently. 'idle' | 'saving' | 'saved' | 'error'.
  const [saveStatus, setSaveStatus] = useState("idle");
  const saveStatusClearRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const [jobid, setJobid] = useState(null);
  // S-66: markup on other revisions (server counts), legacy markup on this page, and a drawing that
  // changed on the server while this viewer was open (then saving is blocked until it is reopened).
  const [hidden, setHidden] = useState(null);
  const [legacyOnPage, setLegacyOnPage] = useState(false);
  const [staleDrawing, setStaleDrawing] = useState(false);
  const [markupReady, setMarkupReady] = useState(false);
  const [renderedKey, setRenderedKey] = useState(null);
  // Codex #75 P1: markup writes (saves and clears) run one at a time, in order, per job (enqueueJobWrite).
  // A save snapshots the page-space master when it runs, so strokes made while it waits are included.
  // While a clear is in flight, drawing and page changes are off; a page load waits for queued writes.
  const saveQueued = useRef(null);        // master object with a queued save that has not started yet
  const [clearing, setClearing] = useState(false);
  const mayMarkup = !!canEdit && !!drawingFile && !staleDrawing && markupReady && !clearing
    && renderedKey === `${docSeqRef.current}|${page}|${scale}`;
  const enqueueWrite = useCallback((fn) => enqueueJobWrite(jobId, fn), [jobId]);

  // ── Load PDF ──────────────────────────────────────────────────────────────
  // Codex #75 r1 P1: a new url / drawingFile invalidates the loaded PDF, its markup and any markup load in
  // flight before the new PDF loads (numPages goes to 0, so the same page count still reloads markup).
  useEffect(() => {
    let cancelled = false;
    let loadedDoc = null;
    // S-66 (#75): pdfRef and numPages are reset just before load() below (S-68, #74).
    pdfFileRef.current = null;
    masterRef.current = null;
    loadSeq.current += 1;
    setMarkupReady(false);
    setStaleDrawing(false);
    async function load() {
      try {
        setStatus("Loading PDF…");
        const lib = await getPdfjs();
        if (!url || typeof url !== "string") {
          setStatus("No drawing PDF available for this job.");
          return;
        }
        // S-68: /uploads/job_drawings is gated by the backend (Bearer token +
        // company check). PDF.js's own { url } loader sends no Authorization
        // header, so fetch the bytes with the token first and hand PDF.js the
        // data. No object URL is created, so there is nothing to revoke.
        const data = await fetchAuthedMedia(url, "arraybuffer");
        if (cancelled) return;
        const doc = await lib.getDocument({ data: new Uint8Array(data) }).promise;
        if (cancelled) { doc.destroy(); return; }
        loadedDoc = doc;
        pdfRef.current = doc;
        pdfFileRef.current = drawingFile || null;
        docSeqRef.current += 1;
        setNumPages(doc.numPages);
        setStatus("");
      } catch (e) {
        if (cancelled) return;
        const st = e?.response?.status;
        setStatus(
          st === 401 || st === 403
            ? "Couldn't load the drawing: please sign in again."
            : st === 404
            ? "Drawing not found, or it belongs to another company."
            : "Failed to load PDF: " + (e?.message || "unknown error")
        );
      }
    }
    pdfRef.current = null;
    setNumPages(0);
    load();
    return () => {
      cancelled = true;
      // Free the parsed document (and its worker resources) when the viewer
      // closes or the drawing changes.
      if (loadedDoc) {
        try { loadedDoc.destroy(); } catch (_) { /* already gone */ }
        if (pdfRef.current === loadedDoc) pdfRef.current = null;
        loadedDoc = null;
      }
    };
  }, [url, drawingFile]);

  // ── Paint markup (W-27) ───────────────────────────────────────────────────
  // Draws the page-space master onto the screen canvas, scaled to the current zoom.
  const paintMarkup = useCallback(() => {
    const annot = annotRef.current;
    if (!annot) return;
    const ctx = annot.getContext("2d");
    ctx.clearRect(0, 0, annot.width, annot.height);
    const m = masterRef.current;
    if (m && m.page === page && annot.width > 0 && annot.height > 0) {
      ctx.drawImage(m.canvas, 0, 0, annot.width, annot.height);
    }
  }, [page]);

  // ── Render PDF page ───────────────────────────────────────────────────────
  // Zoom changes re-render the PDF and repaint the markup from the master; they do not reload it.
  const renderPage = useCallback(async () => {
    const doc    = pdfRef.current;
    const canvas = pdfCanvasRef.current;
    const annot  = annotRef.current;
    if (!doc || !canvas || !annot) return;
    const key = `${docSeqRef.current}|${page}|${scale}`;
    const seq = ++renderSeqRef.current;
    setRenderedKey(null);

    try {
      const pdfPage  = await doc.getPage(page);
      if (seq !== renderSeqRef.current || pdfRef.current !== doc) return;
      // Stop the render still drawing on this canvas (if any) before resizing and reusing it.
      const prev = renderTaskRef.current;
      if (prev) {
        try { prev.cancel(); } catch (_) { /* already finished */ }
        try { await prev.promise; } catch (_) { /* cancelled: expected */ }
      }
      if (seq !== renderSeqRef.current || pdfRef.current !== doc) return;
      const viewport = pdfPage.getViewport({ scale });
      canvas.width   = viewport.width;
      canvas.height  = viewport.height;
      annot.width    = viewport.width;
      annot.height   = viewport.height;
      paintMarkup();

      const task = pdfPage.render({ canvasContext: canvas.getContext("2d"), viewport });
      renderTaskRef.current = task;
      try {
        await task.promise;
      } finally {
        if (renderTaskRef.current === task) renderTaskRef.current = null;
      }
      // Only the latest render, of the document still shown, enables markup.
      if (seq === renderSeqRef.current && pdfRef.current === doc) setRenderedKey(key);
    } catch (e) {
      // A cancelled render (superseded by a newer one) is expected and not an error.
      // eslint-disable-next-line no-console
      if (seq === renderSeqRef.current && e?.name !== "RenderingCancelledException") console.error("PDF page render failed:", e);
    }
  }, [page, scale, paintMarkup]);

  useEffect(() => { if (pdfRef.current) renderPage(); }, [renderPage]);
  useEffect(() => { if (numPages > 0)   renderPage(); }, [numPages, renderPage]);

  // ── Load markup for the page (S-66, W-27) ─────────────────────────────────
  // The server returns only markup drawn on the job's current drawing, and counts what it hides.
  // Drawing is enabled only once this page's markup has loaded, so a save never replaces markup the
  // viewer did not load.
  const loadMarkup = useCallback(async () => {
    const doc = pdfRef.current;
    if (!doc || pdfFileRef.current !== (drawingFile || null)) return;
    const seq = ++loadSeq.current;
    setMarkupReady(false);
    setLegacyOnPage(false);
    masterRef.current = null;
    paintMarkup();
    // Codex #75 P1 / r2 P1: read only after this job's queued writes (this viewer's, or a closed
    // viewer's) have reached the server.
    await jobWritesSettled(jobId);
    if (seq !== loadSeq.current) return;
    try {
      const pdfPage = await doc.getPage(page);
      const natural = pdfPage.getViewport({ scale: 1 });
      const { w, h } = masterSize(natural.width, natural.height);
      const master = document.createElement("canvas");
      master.width = w;
      master.height = h;
      // Codex WARNING fix (kept): ApiCall has a 30 s timeout.
      const res = await ApiCall.get(`/annotations/${jobId}/${page}`);
      if (seq !== loadSeq.current || pdfRef.current !== doc) return;
      const data = res?.data || {};
      setHidden(data.hidden || null);
      if (!drawingFile || data.drawingFile !== drawingFile) {
        // The job's drawing on the server is not the one on screen (replaced or removed meanwhile).
        setStaleDrawing(true);
        return;
      }
      const parsed = parseMarkup(data.canvasData);
      if (parsed && parsed.kind === "legacy") setLegacyOnPage(true);
      if (parsed && parsed.kind === "page") {
        const img = new Image();
        await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject; img.src = parsed.png; });
        if (seq !== loadSeq.current || pdfRef.current !== doc) return;
        master.getContext("2d").drawImage(img, 0, 0, w, h);
      }
      masterRef.current = { canvas: master, page };
      paintMarkup();
      setMarkupReady(true);
    } catch (err) {
      if (seq !== loadSeq.current) return;
      // eslint-disable-next-line no-console
      console.error("Annotation load failed:", err);
      setStatus("Could not load the markup for this page. Markup is off; close and reopen the drawing to retry.");
    }
  }, [page, jobId, drawingFile, paintMarkup]);

  useEffect(() => { if (numPages > 0) loadMarkup(); }, [numPages, loadMarkup]);

  // ── Save annotations ──────────────────────────────────────────────────────
  // Codex WARNING fix: previously fire-and-forget PUT with silent .catch.
  // Users could lose markup on a network blip with no indication. Now we
  // surface saveStatus, use ApiCall (30s timeout), and clear the status
  // chip after 2s on success / 5s on error so the user sees the outcome.
  // S-66: the save names the drawing on screen; 409 means the drawing changed (nothing was saved).
  // W-27: the page-space master is saved, not the screen canvas.
  const handleWriteError = useCallback((err, what) => {
    if (err?.response?.status === 409) {
      setStaleDrawing(true);
      setSaveStatus("stale");
    } else {
      setSaveStatus("error");
      // eslint-disable-next-line no-console
      console.error(`Annotation ${what} failed:`, err);
      // Stay visible longer on error so the user notices.
      saveStatusClearRef.current = setTimeout(() => setSaveStatus("idle"), 5000);
    }
  }, []);

  const saveAnnotations = useCallback(() => {
    const m = masterRef.current;
    if (!m || !drawingFile) return;
    if (saveQueued.current === m) return; // the queued save will include this stroke
    saveQueued.current = m;
    const file = drawingFile;
    return enqueueWrite(async () => {
      if (saveQueued.current === m) saveQueued.current = null;
      const canvasData = JSON.stringify({
        v: 2, space: "pdf-page", w: m.canvas.width, h: m.canvas.height, png: m.canvas.toDataURL("image/png"),
      });
      if (saveStatusClearRef.current) clearTimeout(saveStatusClearRef.current);
      setSaveStatus("saving");
      try {
        await ApiCall.put(`/annotations/${jobId}/${m.page}`, { canvasData, drawingFile: file });
        if (masterRef.current === m) setLegacyOnPage(false);
        setSaveStatus("saved");
        saveStatusClearRef.current = setTimeout(() => setSaveStatus("idle"), 2000);
      } catch (err) {
        handleWriteError(err, "save");
      }
    });
  }, [jobId, drawingFile, enqueueWrite, handleWriteError]);

  // Clear the timer on unmount so we don't setState after unmount.
  useEffect(() => () => {
    if (saveStatusClearRef.current) clearTimeout(saveStatusClearRef.current);
  }, []);

  // ── Canvas coordinate helper ──────────────────────────────────────────────
  function canvasPoint(clientX, clientY) {
    const annot = annotRef.current;
    const rect  = annot.getBoundingClientRect();
    return {
      x: (clientX - rect.left) * (annot.width  / rect.width),
      y: (clientY - rect.top)  * (annot.height / rect.height),
    };
  }

  // ── Drawing context setup ─────────────────────────────────────────────────
  // k scales widths from screen pixels to the target canvas (1 on screen; master width / screen width on the master).
  function applyStrokeStyle(ctx, pressure, k = 1) {
    const p = Math.max(0.1, Math.min(1, pressure));
    if (tool === TOOLS.HIGHLIGHT) {
      ctx.globalAlpha               = 0.35;
      ctx.globalCompositeOperation  = "source-over";
      ctx.strokeStyle               = hlColour;
      ctx.lineWidth                 = BASE_WIDTH.highlight * (0.7 + p * 0.3) * k;
      ctx.lineCap                   = "square";
      ctx.lineJoin                  = "round";
    } else if (tool === TOOLS.ERASER) {
      ctx.globalCompositeOperation  = "destination-out";
      ctx.lineWidth                 = BASE_WIDTH.eraser * (0.6 + p * 0.8) * k;
      ctx.lineCap                   = "round";
      ctx.lineJoin                  = "round";
    } else {
      // Apple Pencil — pressure-sensitive width
      ctx.globalAlpha               = 1;
      ctx.globalCompositeOperation  = "source-over";
      ctx.strokeStyle               = penColour;
      ctx.lineWidth                 = BASE_WIDTH.pencil * (0.5 + p * 2.0) * k;
      ctx.lineCap                   = "round";
      ctx.lineJoin                  = "round";
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Unified Pointer Event handler
  //   pen / stylus → draw
  //   mouse        → draw
  //   touch (1)    → pan (scroll the container)
  //   touch (2)    → pinch-to-zoom
  // ─────────────────────────────────────────────────────────────────────────

  const handlePointerDown = useCallback((e) => {
    const annot  = annotRef.current;
    // eslint-disable-next-line no-unused-vars
    const scroll = scrollRef.current;
    if (!annot) return;

    annot.setPointerCapture(e.pointerId);

    if (e.pointerType === "touch") {
      touchPointers.current.set(e.pointerId, { clientX: e.clientX, clientY: e.clientY });

      if (touchPointers.current.size === 2) {
        // Start pinch
        const [a, b] = Array.from(touchPointers.current.values());
        pinchInitDist.current  = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
        pinchInitScale.current = null; // will capture current scale on first move
      }
      // Cancel any active drawing if a second finger appears
      if (isDrawing.current) {
        isDrawing.current   = false;
        drawPointerId.current = null;
      }
      return;
    }

    // Pencil / mouse — draw. S-66: only with edit_job, on the current drawing, once the page's markup has
    // loaded (otherwise the drawing is view-only; touch still pans and pinches).
    if (!mayMarkup) return;
    if (tool === TOOLS.TEXT) {
      const pt = canvasPoint(e.clientX, e.clientY);
      setTextPos(pt);
      setShowTextBox(true);
      return;
    }

    e.preventDefault();
    drawPointerId.current  = e.pointerId;
    isDrawing.current      = true;
    lastPoint.current      = canvasPoint(e.clientX, e.clientY);
    lastPressure.current   = e.pressure > 0 ? e.pressure : 0.5;

    if (e.pointerType === "pen") setPencilDetected(true);

    const ctx = annot.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
  }, [tool, mayMarkup]);

  const handlePointerMove = useCallback((e) => {
    if (e.pointerType === "touch") {
      const prev   = touchPointers.current.get(e.pointerId);
      if (!prev) return;

      const curr = { clientX: e.clientX, clientY: e.clientY };

      if (touchPointers.current.size === 1) {
        // Single-finger pan — scroll the container
        const scroll = scrollRef.current;
        if (scroll) {
          scroll.scrollLeft -= (e.clientX - prev.clientX);
          scroll.scrollTop  -= (e.clientY - prev.clientY);
        }
      } else if (touchPointers.current.size === 2) {
        // Pinch-to-zoom
        touchPointers.current.set(e.pointerId, curr);
        const [a, b] = Array.from(touchPointers.current.values());
        const newDist = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);

        if (pinchInitDist.current) {
          setScale((s) => {
            if (pinchInitScale.current === null) pinchInitScale.current = s;
            const ratio = newDist / pinchInitDist.current;
            return Math.min(5, Math.max(0.5, pinchInitScale.current * ratio));
          });
        }
      }

      touchPointers.current.set(e.pointerId, curr);
      return;
    }

    // Pencil / mouse draw move
    if (!isDrawing.current || e.pointerId !== drawPointerId.current) return;
    e.preventDefault();

    const annot    = annotRef.current;
    if (!annot) return;
    const ctx      = annot.getContext("2d");
    const pt       = canvasPoint(e.clientX, e.clientY);
    const pressure = e.pressure > 0 ? e.pressure : lastPressure.current;
    lastPressure.current = pressure;

    // W-27: the same segment on the screen canvas (k = 1) and on the page-space master (scaled).
    const m = masterRef.current;
    const targets = [[ctx, 1]];
    if (m && m.page === page && annot.width > 0) targets.push([m.canvas.getContext("2d"), m.canvas.width / annot.width]);
    for (const [c, k] of targets) {
      c.save();
      // eslint-disable-next-line react-hooks/exhaustive-deps
      applyStrokeStyle(c, pressure, k);
      c.beginPath();
      if (tool === TOOLS.ERASER) {
        c.arc(pt.x * k, pt.y * k, BASE_WIDTH.eraser * (0.6 + pressure * 0.8) * k / 2, 0, Math.PI * 2);
        c.fill();
      } else {
        c.moveTo(lastPoint.current.x * k, lastPoint.current.y * k);
        c.lineTo(pt.x * k, pt.y * k);
        c.stroke();
      }
      c.restore();
    }
    lastPoint.current = pt;
  }, [tool, penColour, hlColour, applyStrokeStyle, page]);

  const handlePointerUp = useCallback((e) => {
    if (e.pointerType === "touch") {
      touchPointers.current.delete(e.pointerId);
      if (touchPointers.current.size < 2) {
        pinchInitDist.current  = null;
        pinchInitScale.current = null;
      }
      return;
    }
    if (!isDrawing.current || e.pointerId !== drawPointerId.current) return;
    isDrawing.current     = false;
    drawPointerId.current = null;
    saveAnnotations();
  }, [saveAnnotations]);

  const handlePointerCancel = useCallback((e) => {
    touchPointers.current.delete(e.pointerId);
    if (e.pointerId === drawPointerId.current) {
      isDrawing.current     = false;
      drawPointerId.current = null;
    }
    pinchInitDist.current  = null;
    pinchInitScale.current = null;
  }, []);

  // ── Text stamp ────────────────────────────────────────────────────────────
  function stampText() {
    if (!textInput.trim() || !mayMarkup) { setShowTextBox(false); return; }
    const annot = annotRef.current;
    const m = masterRef.current;
    // W-27: on the screen canvas and, scaled, on the page-space master.
    const targets = [[annot.getContext("2d"), 1]];
    if (m && m.page === page && annot.width > 0) targets.push([m.canvas.getContext("2d"), m.canvas.width / annot.width]);
    for (const [ctx, k] of targets) {
      ctx.save();
      ctx.font      = `bold ${Math.round(18 * k)}px Arial`;
      ctx.fillStyle = penColour;
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = "source-over";
      ctx.fillText(textInput, textPos.x * k, textPos.y * k);
      ctx.restore();
    }
    saveAnnotations();
    setTextInput("");
    setShowTextBox(false);
  }

  // ── Clear page ────────────────────────────────────────────────────────────
  // S-66: the clear names the drawing on screen; the server keeps the cleared markup in its audit log.
  // The screen is cleared only once the server has cleared it.
  function clearPage() {
    const annot = annotRef.current;
    const m = masterRef.current;
    if (!annot || !m || !mayMarkup) return;
    if (!window.confirm("Clear the markup on this page? (The cleared markup is kept in the audit log.)")) return;
    // Codex #75 P1: queued behind any pending save; drawing and page changes are off until it finishes,
    // and only this page's master (m) is cleared when it does.
    setClearing(true);
    const file = drawingFile;
    enqueueWrite(async () => {
      // Codex WARNING fix: switched from raw fetch to ApiCall (30s timeout)
      // and surface failure via saveStatus so users notice if the server
      // didn't actually drop their annotations.
      if (saveStatusClearRef.current) clearTimeout(saveStatusClearRef.current);
      setSaveStatus("saving");
      try {
        await ApiCall.delete(`/annotations/${jobId}/${m.page}`, { params: { drawingFile: file } });
        m.canvas.getContext("2d").clearRect(0, 0, m.canvas.width, m.canvas.height);
        if (masterRef.current === m) { paintMarkup(); setLegacyOnPage(false); }
        setSaveStatus("saved");
        saveStatusClearRef.current = setTimeout(() => setSaveStatus("idle"), 2000);
      } catch (err) {
        handleWriteError(err, "clear");
      } finally {
        setClearing(false);
      }
    });
  }

  // ── Navigation ────────────────────────────────────────────────────────────
  function prevPage() { if (page > 1 && !clearing)        setPage((p) => p - 1); }
  function nextPage() { if (page < numPages && !clearing) setPage((p) => p + 1); }

  return (
    <div style={S.overlay}>

      {/* ── Toolbar ── */}
      <div style={S.toolbar}>
        <button style={S.btn(false)} onClick={onClose}>✕ Close</button>
        {pencilDetected && <span style={S.pencilBadge}>✏ Pencil</span>}
        <div style={S.sep} />

        {/* Page nav */}
        <button style={S.btn(false)} onClick={prevPage} disabled={page <= 1 || clearing}>‹</button>
        <span style={S.pageInfo}>{page} / {numPages || "…"}</span>
        <button style={S.btn(false)} onClick={nextPage} disabled={page >= numPages || clearing}>›</button>
        <div style={S.sep} />

        {/* Zoom */}
        <button style={S.btn(false)} onClick={() => setScale((s) => Math.min(5, s + 0.25))}>＋</button>
        <span style={{ color: "#999", fontSize: 12 }}>{Math.round(scale * 100)}%</span>
        <button style={S.btn(false)} onClick={() => setScale((s) => Math.max(0.5, s - 0.25))}>－</button>
        <div style={S.sep} />

        {/* Markup tools: edit_job only (S-66; the server checks the same permission, live). */}
        {mayMarkup ? (
          <>
          {/* Tools */}
          <button style={S.btn(tool === TOOLS.PENCIL)}    onClick={() => setTool(TOOLS.PENCIL)}>✏️ Pen</button>
          <button style={S.btn(tool === TOOLS.HIGHLIGHT)} onClick={() => setTool(TOOLS.HIGHLIGHT)}>🖊 Highlight</button>
          <button style={S.btn(tool === TOOLS.TEXT)}      onClick={() => setTool(TOOLS.TEXT)}>T Text</button>
          <button style={S.btn(tool === TOOLS.ERASER)}    onClick={() => setTool(TOOLS.ERASER)}>◻ Erase</button>
          <div style={S.sep} />

          {/* Colour swatches */}
          {tool === TOOLS.HIGHLIGHT
            ? HIGHLIGHT_COLOURS.map((c) => (
                <div key={c} style={S.swatch(c, c === hlColour)} onClick={() => setHlColour(c)} />
              ))
            : (tool === TOOLS.PENCIL) && PEN_COLOURS.map((c) => (
                <div key={c} style={S.swatch(c, c === penColour)} onClick={() => setPenColour(c)} />
              ))
          }
          <div style={S.sep} />

          <button
            style={{ ...S.btn(false), color: "#ff6b6b", borderColor: "#ff6b6b" }}
            onClick={clearPage}
          >
            🗑 Clear page
          </button>
          </>
        ) : (
          <span style={{ ...S.pageInfo, color: "#ffcc80" }}>
            {!canEdit
              ? "View only (editing markup needs the Edit Job permission)"
              : staleDrawing
              ? "Markup off: this drawing was replaced or removed. Close and reopen it."
              : !drawingFile
              ? "Markup off: the drawing file is unknown. Close and reopen it."
              : clearing
              ? "Clearing…"
              : status
              ? "Markup off (see below)."
              : "Loading markup…"}
          </span>
        )}

        {/* Codex WARNING fix: surface annotation save status so users don't
            lose markup silently. */}
        {saveStatus !== "idle" && (
          <span
            role="status"
            aria-live="polite"
            style={{
              marginLeft: 8,
              padding: "2px 10px",
              borderRadius: 4,
              fontSize: 12,
              fontWeight: 600,
              background:
                saveStatus === "saving"
                  ? "#1e3a5f"
                  : saveStatus === "saved"
                  ? "#1e4d2b"
                  : "#5f1e1e",
              color:
                saveStatus === "saving"
                  ? "#9ec5fe"
                  : saveStatus === "saved"
                  ? "#a3d9b1"
                  : "#ffb4b4",
              border: `1px solid ${
                saveStatus === "saving"
                  ? "#3a6a9c"
                  : saveStatus === "saved"
                  ? "#3a7d4b"
                  : "#a04040"
              }`,
            }}
          >
            {saveStatus === "saving"
              ? "Saving…"
              : saveStatus === "saved"
              ? "Saved ✓"
              : saveStatus === "stale"
              ? "Not saved: the drawing was replaced. Close and reopen it."
              : "Save failed — retry by drawing again"}
          </span>
        )}
      </div>

      {/* ── Scroll container — receives scrollLeft/scrollTop panning ── */}
      <div ref={scrollRef} style={S.scrollContainer}>
        <div style={{ position: "relative", display: "inline-block" }}>
          <canvas ref={pdfCanvasRef} style={S.pdfCanvas} />
          <canvas
            ref={annotRef}
            style={S.annotCanvas}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
          />
        </div>
      </div>

      {/* ── Text input popup ── */}
      {showTextBox && (
        <div style={{
          position: "fixed",
          bottom: 60,
          left: "50%",
          transform: "translateX(-50%)",
          background: "#333",
          border: "1px solid #555",
          borderRadius: 8,
          padding: 12,
          display: "flex",
          gap: 8,
          zIndex: 10001,
        }}>
          <input
            autoFocus
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && stampText()}
            placeholder="Type note, press Enter"
            style={{
              background: "#444",
              color: "#fff",
              border: "1px solid #666",
              borderRadius: 4,
              padding: "6px 10px",
              fontSize: 14,
              width: 240,
            }}
          />
          <button style={S.btn(true)} onClick={stampText}>Stamp</button>
          <button style={S.btn(false)} onClick={() => setShowTextBox(false)}>Cancel</button>
        </div>
      )}

      {/* ── S-66: hidden markup from other revisions, and legacy (pre-W-27) markup on this page ── */}
      {(hiddenNotice(hidden) || legacyOnPage) && (
        <div role="note" style={{ ...S.statusBar, color: "#ffcc80", background: "#2a2112" }}>
          {hiddenNotice(hidden)}
          {legacyOnPage && ` Markup on this page was saved in an older format at an unknown zoom and cannot be aligned, so it is not shown.${mayMarkup ? " Drawing here replaces it (the old markup stays in the audit log)." : ""}`}
        </div>
      )}

      {/* ── Status bar ── */}
      {status && <div style={S.statusBar}>{status}</div>}
    </div>
  );
}
